import { AddOutlined, LocationOff, LocationOn } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { VIEW } from "../../../utils/breakpoint";
import { COLOR } from "../../../utils/colors";
import { defaultHeaderStyle } from "../../robots/components/v2/_style";
import { dummyData } from "./dummy_data";
import { useListConfigBindMap, useUnbindMapMutation } from "../../../hooks/config-page/bind-map";


export default function BindLayoutMapPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [selectedWantToUnbind, setSelectedWantToUnbind] = useState<string>('')

  const [step, setStep] = useState(1)
  const onCreateNew = () => {
    navigate('/config/bind-layout-maps/create')
  }

  const { data: dataList, refetch } = useListConfigBindMap({
    page,
    page_size: rowsPerPage,
    search: searchTerm
  })

  const { trigger: submitUnbindMap } = useUnbindMapMutation({
    onSuccess: () => {
      setSelectedWantToUnbind('')
      refetch()
    }
  })

  const listBindMaps = dataList?.data || []
  const totalPages = dataList?.num_pages || 0

  const handleBind = (map_uuid: string) => {
    navigate('/config/bind-layout-maps/create?robot_map_uuid=' + map_uuid)
  }

  const handleUnbind = (map_uuid: string) => {
    submitUnbindMap({ map_uuid })
    setSelectedWantToUnbind('')
  }

  return (
    <Box sx={(theme) => ({
      paddingY: '34px',
      color: COLOR.neutral[900],
      [theme.breakpoints.up(VIEW.PHONE)]: {
        paddingX: '24px',
      },
      [theme.breakpoints.up(VIEW.TABLET)]: {
        paddingX: '44px',
      }
    })}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: COLOR.neutral[900]
              }}
            >
              Bind Robot Map GIS Points
            </Typography>
          </Box>
          <Button onClick={() => onCreateNew?.()} variant="outlined" startIcon={<AddOutlined />}>
            Bind New Robot Map GIS Points
          </Button>
        </Box>
        <Box sx={{ display: 'flex', mt: '10px' }}>
          <TextField
            id="search-store-name"
            variant="outlined"
            size="small"
            placeholder='Search Robot Map'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: 'auto',
              backgroundColor: 'white',
              height: "40px",
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <GridSearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
      <Paper elevation={2} sx={{ display: 'flex', flexDirection: 'column', maxHeight: "67vh", borderRadius: "10px", border: "1px solid #E0E0E0", m: '20px auto' }}>
        <TableContainer sx={{ backgroundColor: "#F5F5F5", alignItems: "center", overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead sx={{ width: "100%", margin: "auto" }}>
              <TableRow sx={{ width: "100%", margin: "auto" }}>
                {['No', 'Robot Name', 'Floor Map', 'Action'].map((column, index) => (
                  <TableCell key={index} align="center" sx={{ ...defaultHeaderStyle, textTransform: 'capitalize' }}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {listBindMaps.map((robotMap, index) => (
                <TableRow key={index} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: COLOR.gray[100] } }}>
                  <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{index + 1}</TableCell>
                  <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{robotMap.robot_detail?.robot_name}</TableCell>
                  <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{robotMap.map_name_display}</TableCell>
                  <TableCell align="center" sx={{ ...defaultHeaderStyle }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {robotMap.gis_transformations && (
                        <Button onClick={() => setSelectedWantToUnbind(robotMap.map_uuid)} variant="outlined" color="error" startIcon={<LocationOff />}>
                          UNBIND
                        </Button>
                      )}
                      {!robotMap.gis_transformations && (
                        <Button onClick={() => robotMap.robot && handleBind(robotMap.map_uuid)} variant="outlined" startIcon={<LocationOn />}>
                          BIND
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {listBindMaps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: COLOR.gray[500] }}>
                    No Selected Robot Maps data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={100}
          rowsPerPage={10}
          page={1}
          onPageChange={(e, num) => { }}
          // onRowsPerPageChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value || '10'))}
          sx={{ flexShrink: 0, boxShadow: "none" }} // Prevent pagination from shrinking
        />
      </Paper>

      <Dialog
        open={selectedWantToUnbind !== ''}
        onClose={() => setSelectedWantToUnbind('')}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure want to unbind this Robot Map?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action will unbind the selected Robot Map and can't be undone. Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedWantToUnbind('')} autoFocus>Cancel</Button>
          <Button onClick={() => handleUnbind(selectedWantToUnbind)} variant="contained" color="error">
            Yes, Unbind
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}